/* Global imports. */
import React from "react";
import { graphql } from "gatsby";

/* Component imports. */
import KontentRichText from "@components/KontentRichText";
import Layout from "@components/Layout";
import SEO from "@components/SEO";

/* Model imports. */
import { KontentTemplateHome } from "@models/kontent";
import { PanMacmillanEdition } from "@src/models/panmacmillan";

const Template = ({ data }: any) => {
  const home: KontentTemplateHome = data.allKontentItemTemplateHome.nodes[0].elements;
  const homeEditions: Array<PanMacmillanEdition> = data.allPanMacmillanEdition.nodes;

  return (
    <Layout>
      <main id="home" className="pt-8">
        <div className="container-main kontent--richtext">
          {home.main_content && <KontentRichText value={home.main_content} editions={homeEditions} referringSlug="/" />}
        </div>
      </main>
    </Layout>
  );
};

export default Template;

export const Head = ({data}) => {
  const home: KontentTemplateHome = data.allKontentItemTemplateHome.nodes[0].elements;
  return (
    <SEO 
      title={home.seo__title.value} 
      contentType="home" 
      description={home.seo__description.value} 
      url="/"  
      openGraphTitle={home.seo__open_graph_title.value}
      openGraphDescription={home.seo__open_graph_description.value}
      twitterCardTitle={home.seo__twitter_cards_title.value}
      twitterCardDescription={home.seo__twitter_cards_description.value}
      image={home.seo__open_graph_image.value && home.seo__open_graph_image.value[0] && home.seo__open_graph_image.value[0].url}
    />
  )
}

/* GraphQL schema. */
export const query = graphql`
query homeTemplateQuery (
  $featuredEditions: [String]
) {
    allKontentItemTemplateHome(filter: {system: {codename: {eq: "home"}}}) {
      nodes {
        system {
          codename
        }
        elements {
          seo__title {
            value
          }
          seo__keywords {
            value
          }
          seo__description {
            value
          }
          seo__canonical_url {
            value
          }
          seo__open_graph_title {
            value
          }
          seo__open_graph_description {
            value
          }
          seo__open_graph_image {
            value {
              url
              name
              description
              width
              height
            }
          }
          seo__twitter_cards_title {
            value
          }
          seo__twitter_cards_description {
            value
          }
            main_content {
                value
                modular_content {
                    id
                    system {
                      type
                      name
                      codename
                    }
                    ... on kontent_item_instagram_embed__ {
                        ... modularKontentItemInstagramEmbed
                    }
                    ... on kontent_item_event {
                      ... kontentItemEvent
                    }
                    ... on kontent_item_inline_image {
                      ... modularKontentItemInlineImage
                    }
                    ... on kontent_item_don_t_miss {
                      ... modularKontentItemDontMiss
                    }
                    ... on kontent_item_banner {
                      id
                      elements {
                        title {
                          value
                        }
                        text {
                          value
                        }
                        button_url {
                          value
                        }
                        button_text {
                          value
                        }
                        background_image_placement {
                          value {
                            codename
                          }
                        }
                        text_placement {
                          value {
                            codename
                          }
                        }
                        background_image {
                          value {
                            url
                            description
                          }
                        }
                        width {
                          value {
                            codename
                          }
                        }
                        type {
                          value {
                            codename
                          }
                        }
                        
                        background_colour_left {
                          value
                        }
                        background_colour_right {
                          value
                        }
                        text_colour {
                          value
                        }
                        page_hero {
                          value {
                            codename
                          }
                        }
                        component_id {
                          value
                        }
                      }
                    }
                    ... on kontent_item_banner_carousel {
                      ... modularKontentItemBannerCarousel
                    }
                    ... on kontent_item_book_spotlight_collection {
                      id
                      elements {
                        title {
                          value
                        }
                        introduction {
                          value
                        }
                        text_alignment {
                          value {
                            codename
                          }
                        }
                        books_per_row {
                          value {
                            codename
                            name
                          }
                        }
                        elements {
                          value {
                            codename
                          }
                        }
                        book_spotlights {
                          value {
                            ... on kontent_item_book_spotlight {
                              id
                              elements {
                                title {
                                  value
                                }
                                contributor_name {
                                  value
                                }
                                isbn {
                                  value
                                }
                                is_this_a_pan_macmillan_book_ {
                                  value {
                                    codename
                                  }
                                }
                                alternative_book_description {
                                  value
                                }
                                alternative_jacket_image {
                                  value {
                                    url
                                  }
                                }
                                custom_url {
                                  value
                                }
                              }
                            }
                          }
                        }
                      }
                    }
                    ... on kontent_item_isbn_list_ {
					  ... modularKontentItemIsbnList
					}
                    ... on kontent_item_featured_articles {
                        id
                        elements {
                          title {
                            value
                          }
                          format {
                            name
                            type
                            value {
                              codename
                              name
                            }
                          }
                          featured_articles {
                            name
                            type
                            value {
                              ... on kontent_item_article {
                                id
                                fields {
                                  slug
                                }
                                elements {
                                  url_slug {
                                    value
                                  }
                                  title {
                                    value
                                  }
                                  blurb {
                                    value
                                  }
                                  display_date {
                                    value(formatString: "DD/MM/YYYY")
                                  }
                                  article_categories {
                                    value {
                                      name
                                    }
                                  }
                                  image {
                                    value {
                                      url
                                      description
                                      name
                                    }
                                  }
                                }
                              }
                            }
                          }
                          component_id {
                            value
                          }
                        }
                      }
                      ... on kontent_item_banner_collection {
                        id
                        elements {
                          banners {
                            value {
                              ... on kontent_item_banner {
                                elements {
                                  title {
                                    value
                                  }
                                  text {
                                    value
                                  }
                                  styling {
                                    value {
                                      id
                                      ... on kontent_item_styling {
                                        id
                                        elements {
                                          secondary_colour {
                                            value
                                          }
                                          primary_colour {
                                            value
                                          }
                                          background_colour {
                                            value
                                          }
                                        }
                                      }
                                    }
                                  }
                                  button_url {
                                    value
                                  }
                                  button_text {
                                    value
                                  }
                                  background_image_placement {
                                    value {
                                      codename
                                    }
                                  }
                                  text_placement {
                                    value {
                                      codename
                                    }
                                  }
                                  background_image {
                                    value {
                                      url
                                      description
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                      ... on kontent_item_featured_resources {
                        id
                        elements {
                          title {
                            value
                          }
                          subtitle {
                            value
                          }
                          subcopy {
                            value
                          }
                          button_text {
                            value
                          }
                          button_link {
                            value
                          }
                          resources {
                            value {
                              codename
                            }
                          }
                          imprint {
                            value {
                              name
                            }
                          }
                          related_contributors {
                            value
                          }
                          related_isbns {
                            value
                          }
                          related_series {
                            value
                          }
                          resources {
                            taxonomy_group
                            value {
                              codename
                            }
                            type
                          }
                          handpick_resources {
                            value {
                              ... on kontent_item_downloadable_resource {
                                id
                                elements {
                                  title {
                                    value
                                  }
                                  resource {
                                    value {
                                      url
                                      description
                                      size
                                    }
                                  }
                                  image {
                                    value {
                                      url
                                      description
                                      name
                                    }
                                  }
                                  description {
                                    value
                                  }
                                  button_text {
                                    value
                                  }
                                  display_keyline {
                                    value {
                                      codename
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                      ... on kontent_item_external_featured_content {
                        id
                        elements {
                          type {
                            value {
                              codename
                            }
                          }
                          title {
                            value
                          }
                          blurb {
                            value
                          }
                          display_date {
                            value(formatString: "DD/MM/YYYY")
                          }
                          url {
                            value
                          }
                          source {
                            value
                          }
                          image {
                            value {
                              url
                              description
                              name
                            }
                          }
                          image_type {
                            value {
                              codename
                            }
                          }
                          background_colour_left {
                            value
                          }
                          background_colour_right {
                            value
                          }
                          text_colour {
                            value
                          }
                          text_background_colour {
                            value
                          }
                          button_colour {
                            value
                          }
                          button_text_colour {
                            value
                          }
                          url_text {
                            value
                          }
                          youtube {
                            value {
                              ... on kontent_item_youtube_embed {
                                id
                                elements {
                                  title {
                                    value
                                  }
                                  video_id {
                                    value
                                  }
                                }
                              }
                            }
                          }
                          image_position {
                            value {
                              codename
                            }
                          }
                          display_width {
                            value {
                              codename
                            }
                          }
                          preferred_display {
                            value {
                              codename
                            }
                          }
                          article {
                            name
                            type
                            value {
                              ... on kontent_item_article {
                                id
                                fields {
                                  slug
                                }
                                elements {
                                  url_slug {
                                    value
                                  }
                                  title {
                                    value
                                  }
                                  blurb {
                                    value
                                  }
                                  display_date {
                                    value(formatString: "DD/MM/YYYY")
                                  }
                                  article_categories {
                                    value {
                                      name
                                    }
                                  }
                                  image {
                                    value {
                                      url
                                      description
                                      name
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                      ... on kontent_item_banner {
                        id
                        elements {
                          title {
                            value
                          }
                          text {
                            value
                          }
                          styling {
                            value {
                              id
                              ... on kontent_item_styling {
                                id
                                elements {
                                  secondary_colour {
                                    value
                                  }
                                  primary_colour {
                                    value
                                  }
                                  background_colour {
                                    value
                                  }
                                }
                              }
                            }
                          }
                          button_url {
                            value
                          }
                          button_text {
                            value
                          }
                          background_image_placement {
                            value {
                              codename
                            }
                          }
                          text_placement {
                            value {
                              codename
                            }
                          }
                          background_image {
                            value {
                              url
                              description
                            }
                          }
                        }
                      }
                      ... on kontent_item_isbn_list_ {
                        id
                        elements {
                          title {
                            value
                          }
                          preferred_display {
                            value {
                              codename
                            }
                          }
                          isbns {
                            value
                          }
                        }
                      }
                      ... on kontent_item_youtube_collection {
                        id
                        elements {
                          title {
                            value
                          }
                          introduction {
                            value
                          }
                          preferred_items_per_row {
                            value {
                              codename
                            }
                          }
                          youtube_embeds {
                            value {
                              ... on kontent_item_youtube_embed {
                                id
                                elements {
                                  video_id {
                                    value
                                  }
                                  title {
                                    value
                                  }
                                }
                              }
                            }
                          }
                        }
                      }
                      
                      ... on kontent_item_featured_articles {
                          id
                          elements {
                            title {
                              value
                            }
                            format {
                              name
                              type
                              value {
                                codename
                                name
                              }
                            }
                            featured_articles {
                              name
                              type
                              value {
                                ... on kontent_item_article {
                                  id
                                  fields {
                                    slug
                                  }
                                  elements {
                                    url_slug {
                                      value
                                    }
                                    title {
                                      value
                                    }
                                    blurb {
                                      value
                                    }
                                    display_date {
                                      value(formatString: "DD/MM/YYYY")
                                    }
                                    article_categories {
                                      value {
                                        name
                                      }
                                    }
                                    image {
                                      value {
                                        url
                                        description
                                        name
                                      }
                                    }
                                  }
                                }
                              }
                            }
                          }
                        }
                }
                name
                type
                value
            }
          }
        }   
    }
    allPanMacmillanEdition (
      filter: {isbn: {in: $featuredEditions}}
    ) {
      nodes {
        titleFull
        title
        titlePrefix
        workId
        isbn
        fields {
          slug
        }
        isPrimaryEdition
        contributors {
          contributorRole
          contributorDetails {
            personName
          }
        }
        primaryContributorName
        primaryContributorID
        sourceImagePath
        remoteImage {
            publicURL
          }
        dimensions {
          width
          height
        }
        format
		collection {
			collectionIdentifier
			collectionSequenceNumber
			collectionTitle
		  }
      }
    }
}
`